import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Settings.css';
import config from '../config';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';

const subsOrDefault = (r) => ({
  dailyProduction: r.reportSubscriptions?.dailyProduction !== false,
  dailySummary: r.reportSubscriptions?.dailySummary === true,
});

const historyReportLabel = (reportType) => {
  if (reportType === 'dailySummary') return 'Daily summary';
  return 'Production';
};

const hasAnySubscription = (r) =>
  subsOrDefault(r).dailyProduction || subsOrDefault(r).dailySummary;

const Settings = () => {
  const [recipients, setRecipients] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, sendTime: '03:00' });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Add recipient dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    email: '',
    name: '',
    reportSubscriptions: { dailyProduction: true, dailySummary: false },
  });
  
  // Test email dialog
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recipientsRes, settingsRes, historyRes] = await Promise.all([
        axios.get(`${config.backendUrl}/email/recipients`),
        axios.get(`${config.backendUrl}/email/settings`),
        axios.get(`${config.backendUrl}/email/history`)
      ]);

      if (recipientsRes.data.status === 'success') {
        setRecipients(recipientsRes.data.data);
      }
      if (settingsRes.data.status === 'success') {
        setSettings(settingsRes.data.data);
      }
      if (historyRes.data.status === 'success') {
        setEmailHistory(historyRes.data.data);
      }
    } catch (error) {
      showSnackbar('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddRecipient = async () => {
    if (!newRecipient.email) {
      showSnackbar('Email is required', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRecipient.email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    try {
      const response = await axios.post(`${config.backendUrl}/email/recipients`, newRecipient);
      if (response.data.status === 'success') {
        setRecipients([...recipients, response.data.data]);
        setNewRecipient({
          email: '',
          name: '',
          reportSubscriptions: { dailyProduction: true, dailySummary: false },
        });
        setOpenAddDialog(false);
        showSnackbar('Recipient added successfully');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error adding recipient', 'error');
    }
  };

  const handleDeleteRecipient = async (id) => {
    if (!window.confirm('Are you sure you want to remove this recipient?')) {
      return;
    }

    try {
      const response = await axios.delete(`${config.backendUrl}/email/recipients/${id}`);
      if (response.data.status === 'success') {
        setRecipients(recipients.filter(r => r.id !== id));
        showSnackbar('Recipient removed successfully');
      }
    } catch (error) {
      showSnackbar('Error removing recipient', 'error');
    }
  };

  const handleToggleRecipient = async (id, currentStatus) => {
    try {
      const response = await axios.put(`${config.backendUrl}/email/recipients/${id}`, {
        active: !currentStatus
      });
      if (response.data.status === 'success') {
        setRecipients(recipients.map(r =>
          r.id === id ? { ...r, active: !currentStatus } : r
        ));
        showSnackbar(`Recipient ${!currentStatus ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      showSnackbar('Error updating recipient', 'error');
    }
  };

  const handleToggleReportSubscription = async (recipient, field, checked) => {
    try {
      const response = await axios.put(`${config.backendUrl}/email/recipients/${recipient.id}`, {
        reportSubscriptions: {
          ...subsOrDefault(recipient),
          [field]: checked,
        },
      });
      if (response.data.status === 'success') {
        setRecipients(recipients.map(r =>
          r.id === recipient.id ? response.data.data : r
        ));
      }
    } catch (error) {
      showSnackbar('Error updating report preferences', 'error');
    }
  };

  const handleToggleEmailEnabled = async (enabled) => {
    try {
      const response = await axios.put(`${config.backendUrl}/email/settings/schedule`, {
        sendTime: settings.sendTime,
        enabled
      });
      if (response.data.status === 'success') {
        setSettings({ ...settings, enabled });
        showSnackbar(`Automated emails ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      showSnackbar('Error updating settings', 'error');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      showSnackbar('Email is required', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    try {
      setTestSending(true);
      const response = await axios.post(`${config.backendUrl}/email/test`, { email: testEmail });
      if (response.data.status === 'success') {
        showSnackbar('Test email sent successfully!');
        setOpenTestDialog(false);
        setTestEmail('');
        fetchData(); // Refresh history
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error sending test email', 'error');
    } finally {
      setTestSending(false);
    }
  };

  const handleSendDailyReport = async () => {
    if (
      !window.confirm(
        'Send now to everyone who is active and subscribed (daily production and/or daily summary)?'
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${config.backendUrl}/email/send-daily-report`);
      if (response.data.status === 'success') {
        showSnackbar(response.data.message || 'Reports sent successfully!');
        fetchData();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error sending daily report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Email Settings</h1>
      </div>

      {/* Email Schedule Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Email Schedule</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => handleToggleEmailEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={settings.enabled ? 'Automated Emails Enabled' : 'Automated Emails Disabled'}
            />
            
            <Typography variant="body2" color="text.secondary">
              Each recipient receives only the reports they subscribe to below. Schedule:{' '}
              <strong>9:30 AM IST</strong> (4:00 AM UTC)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Quick Actions Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Quick Actions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Production = machine-wise daily production email. Summary = business-day and month rollup table.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendDailyReport}
              disabled={
                recipients.filter((r) => r.active && hasAnySubscription(r)).length === 0
              }
            >
              Send Subscribed Reports Now
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setOpenTestDialog(true)}
            >
              Send Test Email
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Recipients Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Email Recipients</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
            >
              Add Recipient
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Production report</TableCell>
                  <TableCell align="center">Daily summary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No recipients added yet. Click &quot;Add Recipient&quot; to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recipients.map((recipient) => {
                    const sub = subsOrDefault(recipient);
                    return (
                    <TableRow key={recipient.id}>
                      <TableCell>{recipient.name || '-'}</TableCell>
                      <TableCell>{recipient.email}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={sub.dailyProduction}
                          disabled={!recipient.active}
                          onChange={(e) =>
                            handleToggleReportSubscription(
                              recipient,
                              'dailyProduction',
                              e.target.checked
                            )
                          }
                          inputProps={{ 'aria-label': 'Production report' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={sub.dailySummary}
                          disabled={!recipient.active}
                          onChange={(e) =>
                            handleToggleReportSubscription(
                              recipient,
                              'dailySummary',
                              e.target.checked
                            )
                          }
                          inputProps={{ 'aria-label': 'Daily summary report' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={recipient.active ? 'Active' : 'Inactive'}
                          color={recipient.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(recipient.addedAt)}</TableCell>
                      <TableCell align="right">
                        <Switch
                          checked={recipient.active}
                          onChange={() => handleToggleRecipient(recipient.id, recipient.active)}
                          size="small"
                          color="primary"
                        />
                        <IconButton
                          onClick={() => handleDeleteRecipient(recipient.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Email History Card */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Email History</Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Report</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Message ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No email history yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  emailHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDate(entry.sentAt)}</TableCell>
                      <TableCell>{historyReportLabel(entry.reportType)}</TableCell>
                      <TableCell>
                        {entry.recipients.length} recipient{entry.recipients.length !== 1 ? 's' : ''}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          color={entry.status === 'success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {entry.messageId ? entry.messageId.substring(0, 30) + '...' : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Recipient Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add Email Recipient</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name (Optional)"
            type="text"
            fullWidth
            value={newRecipient.name}
            onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={newRecipient.email}
            onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Reports for this recipient
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={newRecipient.reportSubscriptions.dailyProduction}
                onChange={(e) =>
                  setNewRecipient({
                    ...newRecipient,
                    reportSubscriptions: {
                      ...newRecipient.reportSubscriptions,
                      dailyProduction: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Daily production (machine-wise)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRecipient.reportSubscriptions.dailySummary}
                onChange={(e) =>
                  setNewRecipient({
                    ...newRecipient,
                    reportSubscriptions: {
                      ...newRecipient.reportSubscriptions,
                      dailySummary: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Daily summary (rollup table)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddRecipient} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            helperText="A sample daily report will be sent to this email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTestDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={testSending}
          >
            {testSending ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false})}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Settings;

